import { HTTPError } from 'ky';

import { PublicSignerErrorCode } from '@/types/esign';

import type { PublicSignerValidationError } from './publicSignerTypes';

export type PublicSignerLinkState =
  | 'invalid'
  | 'expired'
  | 'superseded'
  | 'completed'
  | 'cancelled'
  | 'rate_limited'
  | 'server_error';

const TERMINAL_LINK_STATES: PublicSignerLinkState[] = [
  'invalid',
  'expired',
  'superseded',
  'completed',
  'cancelled'
];

export function isTerminalLinkState(
  linkState: PublicSignerLinkState | null | undefined
): boolean {
  return Boolean(linkState) && TERMINAL_LINK_STATES.includes(linkState!);
}

export interface PublicSignerError extends Error {
  errorCode?: string | null;
  linkState?: PublicSignerLinkState | null;
  status?: number | null;
  validationErrors?: PublicSignerValidationError[] | null;
}

function isRecord(payload: unknown): payload is Record<string, unknown> {
  return (
    Boolean(payload) && typeof payload === 'object' && !Array.isArray(payload)
  );
}

function extractMessageFromPayload(payload: unknown): string | null {
  if (!isRecord(payload)) {
    return null;
  }

  if (typeof payload.message === 'string') {
    return payload.message;
  }

  const data = payload.data;
  if (isRecord(data) && typeof data.message === 'string') {
    return data.message;
  }

  return null;
}

function extractErrorCodeFromPayload(payload: unknown): string | null {
  if (!isRecord(payload)) {
    return null;
  }

  if (typeof payload.error_code === 'string') {
    return payload.error_code;
  }

  const data = payload.data;
  if (isRecord(data) && typeof data.error_code === 'string') {
    return data.error_code;
  }

  return null;
}

function isValidationError(
  value: unknown
): value is PublicSignerValidationError {
  return (
    isRecord(value) &&
    (value.field_id === undefined ||
      value.field_id === null ||
      typeof value.field_id === 'string') &&
    typeof value.code === 'string' &&
    typeof value.message === 'string'
  );
}

function extractValidationErrorsFromPayload(
  payload: unknown
): PublicSignerValidationError[] | null {
  if (!isRecord(payload)) {
    return null;
  }

  if (
    Array.isArray(payload.validation_errors) &&
    payload.validation_errors.every(isValidationError)
  ) {
    return payload.validation_errors;
  }

  const data = payload.data;
  if (
    isRecord(data) &&
    Array.isArray(data.validation_errors) &&
    data.validation_errors.every(isValidationError)
  ) {
    return data.validation_errors;
  }

  return null;
}

function extractTextPayload(text: string): {
  errorCode: string | null;
  message: string | null;
  validationErrors: PublicSignerValidationError[] | null;
} {
  if (!text.trim()) {
    return {
      errorCode: null,
      message: null,
      validationErrors: null
    };
  }

  try {
    const parsed = JSON.parse(text);
    return {
      errorCode: extractErrorCodeFromPayload(parsed),
      message: extractMessageFromPayload(parsed) ?? text,
      validationErrors: extractValidationErrorsFromPayload(parsed)
    };
  } catch {
    return {
      errorCode: null,
      message: text,
      validationErrors: null
    };
  }
}

function getStatus(error: unknown): number | null {
  if (typeof (error as { status?: unknown })?.status === 'number') {
    return (error as { status: number }).status;
  }

  if (error instanceof HTTPError) {
    return error.response.status;
  }

  const response = (error as { response?: { status?: unknown } })?.response;
  return typeof response?.status === 'number' ? response.status : null;
}

async function extractResponseDetails(error: unknown): Promise<{
  errorCode: string | null;
  message: string | null;
  validationErrors: PublicSignerValidationError[] | null;
}> {
  const response = (error as { response?: Response })?.response;

  if (
    response &&
    typeof response.clone === 'function' &&
    typeof response.text === 'function'
  ) {
    try {
      const json = await response.clone().json();
      return {
        errorCode: extractErrorCodeFromPayload(json),
        message: extractMessageFromPayload(json),
        validationErrors: extractValidationErrorsFromPayload(json)
      };
    } catch {}

    try {
      return extractTextPayload(await response.clone().text());
    } catch {}
  }

  return {
    errorCode: extractErrorCodeFromPayload(error),
    message:
      extractMessageFromPayload(error) ??
      (error instanceof Error ? error.message : null),
    validationErrors: extractValidationErrorsFromPayload(error)
  };
}

function inferLinkState({
  errorCode,
  status
}: {
  errorCode: string | null;
  status: number | null;
}): PublicSignerLinkState | null {
  const normalizedCode = errorCode?.trim().toLowerCase() ?? null;

  if (normalizedCode === PublicSignerErrorCode.Invalid) {
    return 'invalid';
  }

  if (normalizedCode === PublicSignerErrorCode.Expired) {
    return 'expired';
  }

  if (normalizedCode === PublicSignerErrorCode.Superseded) {
    return 'superseded';
  }

  if (normalizedCode === PublicSignerErrorCode.Completed) {
    return 'completed';
  }

  if (normalizedCode === PublicSignerErrorCode.Cancelled) {
    return 'cancelled';
  }

  if (normalizedCode === PublicSignerErrorCode.RateLimited) {
    return 'rate_limited';
  }

  if (normalizedCode === PublicSignerErrorCode.ServerError) {
    return 'server_error';
  }

  if (status === 401) {
    return 'invalid';
  }

  if (status === 410) {
    return 'expired';
  }

  // 409 only fires from the submit endpoint when an operator cancels mid-flight.
  if (status === 409) {
    return 'cancelled';
  }

  if (status === 429) {
    return 'rate_limited';
  }

  if (status !== null && status >= 500) {
    return 'server_error';
  }

  return null;
}

export async function normalizePublicSignerError(
  error: unknown,
  fallback = 'Failed to load this signing session.'
): Promise<PublicSignerError> {
  const status = getStatus(error);
  const { errorCode, message, validationErrors } =
    await extractResponseDetails(error);

  const normalized = new Error(
    message ?? (error instanceof Error ? error.message : fallback)
  ) as PublicSignerError;

  normalized.errorCode = errorCode;
  normalized.linkState = inferLinkState({
    errorCode,
    status
  });
  normalized.status = status;
  normalized.validationErrors = validationErrors;

  return normalized;
}
